import { useState } from "react";
import { Star, LifeBuoy, Maximize2, X } from "lucide-react";
import { PHASES } from "./PhaseTracker";
import { getFlatHelpPrompts } from "../lib/help";

/**
 * PipView V2 — every critical action works INSIDE PiP without leaving it.
 *
 * Fixes for beta-blocker bugs 1-6:
 *  - Full manual phase controls (6 pills) rendered inline. Manual override
 *    always wins — clicking a phase pill fires the parent's setPhase.
 *  - Help panel renders INSIDE PiP (not via Radix Dialog which portals to the
 *    parent window's body and is invisible from PiP).
 *  - V0.3.11: Help panel prompts are now methodology-aware — they react to
 *    what's MISSING / UNDERDEVELOPED in the live state (not a static dict).
 *  - No auto-close: PiP lifecycle is user-controlled only.
 *  - "Dashboard" button opens the full dashboard AS A SIBLING, PiP stays live.
 */
const DISCOVERY_KEYS = [
  "pain_points", "goals", "previous_attempts",
  "cost_of_inaction", "decision_maker", "financial_situation",
];

export default function PipView({ phase, state, transcript, mode = "b2b", isLive, onSetPhase, onMark, onExpand }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [rolodexOpen, setRolodexOpen] = useState(false);
  const discoveryDone = DISCOVERY_KEYS.filter((k) => (state[k] || []).length > 0).length;
  const helpPrompts = getFlatHelpPrompts({ phase, state, transcript, mode });

  return (
    <div
      data-testid="pip-view"
      className="h-full w-full bg-slate-50 text-slate-900 p-2 flex flex-col gap-1.5 overflow-hidden"
      style={{ fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}
    >
      {/* Phase strip — all 6 phases, manual click switches everywhere */}
      <div className="flex gap-1 overflow-x-auto sch-scroll" data-testid="pip-phase-strip">
        {PHASES.map((p, i) => {
          const active = p.id === phase;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSetPhase(p.id)}
              data-testid={`pip-phase-${p.id}`}
              className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
              title={p.full}
            >
              {i + 1} {p.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between px-1">
        <span
          data-testid="pip-discovery-progress"
          className="text-[11px] font-mono font-semibold text-slate-600"
        >
          {discoveryDone}/6 discovery
        </span>
        <button
          type="button"
          onClick={() => setRolodexOpen((v) => !v)}
          data-testid="pip-rolodex-toggle"
          className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900 font-semibold"
        >
          {rolodexOpen ? "Hide" : "Rolodex"}
        </button>
      </div>

      {/* Rolodex: glanceable capture of top insights per category */}
      {rolodexOpen && (
        <div
          data-testid="pip-rolodex"
          className="text-[11px] leading-tight bg-white border border-slate-200 rounded p-1.5 max-h-24 overflow-y-auto sch-scroll"
        >
          {DISCOVERY_KEYS.map((k) => {
            const items = state[k] || [];
            if (!items.length) return null;
            const label = k.replace(/_/g, " ");
            return (
              <div key={k} className="mb-1">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
                <div className="text-slate-800 truncate">{items[0].text}</div>
              </div>
            );
          })}
          {DISCOVERY_KEYS.every((k) => !(state[k] || []).length) && (
            <div className="text-slate-400 italic">No insights captured yet.</div>
          )}
        </div>
      )}

      {/* Help panel — inline, no Radix portal */}
      {helpOpen && (
        <div
          data-testid="pip-help-panel"
          className="text-[11px] leading-snug bg-amber-50 border border-amber-200 rounded p-1.5 max-h-24 overflow-y-auto sch-scroll"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] uppercase tracking-widest text-amber-800 font-semibold">
              {PHASES.find(p => p.id === phase)?.full} prompts
            </span>
            <button onClick={() => setHelpOpen(false)} className="text-amber-800"><X className="h-3 w-3" /></button>
          </div>
          <ul className="space-y-0.5">
            {helpPrompts.length === 0 ? (
              <li className="text-slate-500 italic">Keep listening — no nudge needed yet.</li>
            ) : (
              helpPrompts.map((p, i) => (
                <li key={i} className="text-slate-800">▸ {p}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* MARK MOMENT — dominant */}
      <button
        onClick={onMark}
        disabled={!isLive}
        data-testid="pip-mark-moment"
        className="sch-cta-gold flex-1 min-h-[44px] rounded-lg px-3 flex items-center justify-center gap-2 font-display font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontFamily: '"Chivo", system-ui, sans-serif' }}
      >
        <Star className="h-4 w-4 fill-current" /> MARK MOMENT
      </button>

      <div className="flex gap-1.5">
        <button
          onClick={() => setHelpOpen((v) => !v)}
          data-testid="pip-help"
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-2 py-1.5 text-xs font-medium"
        >
          <LifeBuoy className="h-3.5 w-3.5" /> Help
        </button>
        <button
          onClick={onExpand}
          data-testid="pip-expand"
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 hover:border-slate-500 hover:bg-white text-slate-700 px-2 py-1.5 text-xs font-medium"
          title="Open the full dashboard in the main tab. PiP stays open."
        >
          <Maximize2 className="h-3.5 w-3.5" /> Dashboard
        </button>
      </div>
    </div>
  );
}
